/**
 * Consent store for cookies / third-party tracking (LGPD + GDPR Consent Mode v2).
 *
 * Design notes:
 * - Essentials (auth localStorage, sidebar preference cookie, Stripe/Checkout, Sign-In providers
 *   only while the modal is open) are always allowed — they fall under "legitimate interest"
 *   for both regimes and do not require opt-in.
 * - Analytics (Google Analytics 4) is denied by default and only enabled after explicit consent.
 * - Marketing/ads (Google Ads, remarketing) is denied by default; we keep the flag ready for
 *   future use even though the site doesn't serve ads yet, so the UI and Consent Mode stay honest.
 *
 * The store deliberately has NO React dependency so it can be read during bootstrap (before the
 * tree mounts) by the Google Analytics injector.
 */

const STORAGE_KEY = "ezyhub_cc";

/** Bump when the categories change in a way that requires re-asking the user. */
export const CONSENT_VERSION = 1;

export type ConsentCategory = "analytics" | "marketing";

export interface ConsentState {
  /** Schema version — if the stored value has a lower version we treat it as "no decision". */
  v: number;
  /** True when the user has ever made an explicit choice (accept / reject / preferences save). */
  decided: boolean;
  analytics: boolean;
  marketing: boolean;
  /** ISO 8601, when the decision was last saved. */
  updatedAt: string;
}

const DEFAULT_STATE: ConsentState = {
  v: CONSENT_VERSION,
  decided: false,
  analytics: false,
  marketing: false,
  updatedAt: new Date(0).toISOString(),
};

type Listener = (state: ConsentState) => void;
const listeners = new Set<Listener>();

function safeLocalStorage(): Storage | null {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
}

function parse(raw: string | null): ConsentState {
  if (!raw) return DEFAULT_STATE;
  try {
    const parsed = JSON.parse(raw) as Partial<ConsentState>;
    if (!parsed || typeof parsed !== "object") return DEFAULT_STATE;
    if (parsed.v !== CONSENT_VERSION) return DEFAULT_STATE;
    return {
      v: CONSENT_VERSION,
      decided: Boolean(parsed.decided),
      analytics: Boolean(parsed.analytics),
      marketing: Boolean(parsed.marketing),
      updatedAt: typeof parsed.updatedAt === "string" ? parsed.updatedAt : DEFAULT_STATE.updatedAt,
    };
  } catch {
    return DEFAULT_STATE;
  }
}

export function readConsent(): ConsentState {
  const storage = safeLocalStorage();
  return parse(storage?.getItem(STORAGE_KEY) ?? null);
}

function persist(state: ConsentState) {
  const storage = safeLocalStorage();
  try {
    storage?.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Private mode / quota — consent falls back to session-only (still respected in memory
    // via the listeners below).
  }
}

export function writeConsent(partial: Partial<Omit<ConsentState, "v" | "updatedAt" | "decided">>): ConsentState {
  const current = readConsent();
  const next: ConsentState = {
    ...current,
    ...partial,
    v: CONSENT_VERSION,
    decided: true,
    updatedAt: new Date().toISOString(),
  };
  persist(next);
  listeners.forEach((l) => {
    try {
      l(next);
    } catch {
      // Swallow listener failures — they must not break other subscribers.
    }
  });
  return next;
}

/** Shorthand: accept every non-essential category. */
export function acceptAll(): ConsentState {
  return writeConsent({ analytics: true, marketing: true });
}

/** Shorthand: reject every non-essential category. */
export function rejectAll(): ConsentState {
  return writeConsent({ analytics: false, marketing: false });
}

/** React-friendly subscribe; returns an unsubscribe fn. */
export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
